import React, { SetStateAction, useEffect, useState } from "react";
import { asArray, cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { ActionsType, ActionType } from "./buttons/data-table-actions";
import {
  Useable,
  use,
  useTable,
  useTableItemType,
  useTableRowSelection,
  useTableTotal,
} from "./data-table-context";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { functionalUpdate } from "@tanstack/react-table";

export type FloatingTableActionsProps = {
  className?: string;
};

function SelectedItems({ className }: { className?: string }) {
  const itemType = useTableItemType();
  const table = useTable();
  const isAllSelected = table.getAllSelected();
  const total = useTableTotal();
  const selection = table.getState().rowSelection;
  const selectedCount = Object.values(selection).filter(Boolean).length;
  const excludedCount = Object.values(selection).filter(
    (selected) => !selected,
  ).length;
  const isAllPageRowsSelected = table.getIsAllPageRowsSelected();
  const { t } = useTranslation();
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span className="min-w-10 h-10 bg-azure text-white rounded-md text-sm font-medium flex items-center justify-center">
        {isAllSelected && total?.value
          ? Math.max(0, total.value - excludedCount)
          : selectedCount}
      </span>
      <div className="text-sm font-medium flex flex-col gap-1">
        <div>{`${itemType || t("items")} selected`}</div>
        {(isAllPageRowsSelected || isAllSelected) && total?.value && (
          <div>
            {isAllSelected ? (
              <Button
                variant="link"
                className="p-0 h-auto text-xs font-medium text-azure hover:text-azure/80 bg-transparent"
                onClick={() => {
                  table.setAllSelected(false);
                  table.resetRowSelection();
                }}
              >
                {`Deselect all ${total.value} ${itemType || t("items")} from all pages`}
              </Button>
            ) : (
              <Button
                variant="link"
                className="p-0 h-auto text-xs font-medium text-azure hover:text-azure/80 bg-transparent"
                onClick={() => table.setAllSelected(true)}
              >
                {`Select all ${total.value} ${itemType || t("items")} from all pages`}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ClearSelection({ className }: { className?: string }) {
  const { t } = useTranslation();
  const table = useTable();
  return (
    <button
      onClick={() => {
        table.setAllSelected(false);
        table.resetRowSelection();
      }}
      className={cn(
        "flex items-center justify-center min-w-8 h-8 rounded-md text-aqua hover:text-aqua/80 hover:bg-gray-700 transition-colors",
        className,
      )}
      title={t("clearSelection")}
      aria-label={t("clearSelection")}
    >
      <X size={15} />
    </button>
  );
}

function ActionListButtons({
  className,
  onlyEnabled,
}: {
  className?: string;
  onlyEnabled?: boolean;
}) {
  const { t } = useTranslation();
  const { actions, assignDialogState } = useFloatingTableActions();
  if (!actions) return;
  return (
    <div
      className={cn(
        "flex items-center @xl/data-table:justify-start gap-2 flex-wrap",
        className,
      )}
    >
      {actions.map((action, index) => {
        if (onlyEnabled && action.disabled) return;
        const [opened, setOpened] = assignDialogState(index);
        return (
          <button
            key={index}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-aqua bg-transparent border-0 hover:bg-white/10 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={action.disabled}
            onClick={(ev) => setOpened(true)}
          >
            {action.icon && <action.icon className="w-4 h-4" />}
            {t(action.label)}
          </button>
        );
      })}
    </div>
  );
}

function ActionListDialogs({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { actions, assignDialogState } = useFloatingTableActions();
  if (!actions) return;
  return (
    <div
      className={cn(
        "flex items-center @xl/data-table:justify-start gap-2 flex-wrap",
        className,
      )}
    >
      {actions.map((action, index) => {
        const [opened, setOpened] = assignDialogState(index);
        return (
          <Dialog key={index} open={opened} onOpenChange={setOpened}>
            <DialogContent
              className={cn(
                "min-w-[20vw] max-w-2xl w-auto",
                action.dialogClassName,
              )}
              aria-describedby={undefined}
            >
              {/* <DialogDescription className="sr-only">
                {action.title ? t(action.title) : t(action.label)}
              </DialogDescription> */}
              <DialogHeader className="mb-4 flex-shrink-0">
                <DialogTitle className="mb-4">
                  {action.title ? t(action.title) : t(action.label)}
                </DialogTitle>
                <hr />
              </DialogHeader>
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <action.actionContent onClose={() => setOpened(false)} />
              </div>
            </DialogContent>
          </Dialog>
        );
      })}
    </div>
  );
}

function useFloatingTableActionsState({
  actions: _actions,
}: {
  actions: Useable<ActionsType>;
}) {
  const actions = asArray(use(_actions)) as undefined | ActionType[];
  const [dialogStates, setDialogStates] = useState<boolean[]>(
    new Array(actions?.length).fill(false),
  );

  // Update dialog states when actions change
  useEffect(() => {
    setDialogStates(new Array(actions?.length ?? 0).fill(false));
  }, [actions?.length]);

  const assignDialogState = React.useCallback(
    (index: number) =>
      [
        dialogStates[index] ?? false,
        (a: SetStateAction<boolean>) =>
          setDialogStates((s) => {
            const next = [...s];
            next[index] = functionalUpdate(a, next[index]);
            return next;
          }),
      ] as const,
    [dialogStates],
  );

  return { actions, dialogStates, assignDialogState };
}

type FloatingTableActionsState = ReturnType<
  typeof useFloatingTableActionsState
>;

const FloatingTableActionsContext = React.createContext(
  undefined as unknown as FloatingTableActionsState,
);

export function FloatingTableActionsProvider({
  children,
  ...props
}: Parameters<typeof useFloatingTableActionsState>[0] & {
  children?: React.ReactNode;
}) {
  return (
    <FloatingTableActionsContext.Provider
      value={useFloatingTableActionsState(props)}
      children={children}
    />
  );
}

export function useFloatingTableActions() {
  return React.useContext(FloatingTableActionsContext);
}

export const FloatingTableActions: React.FC<FloatingTableActionsProps> = ({
  className,
}) => {
  const { actions } = useFloatingTableActions();
  const [selection] = useTableRowSelection();
  const selectedCount = Object.values(selection).filter(Boolean).length;
  const table = useTable();
  const isSelectionActive = table.getAllSelected() || selectedCount > 0;
  // Create individual state for each action dialog
  // Clear selection handler
  if (
    !isSelectionActive ||
    !actions ||
    (Array.isArray(actions) && actions.length === 0)
  ) {
    return null;
  }
  return (
    <>
      <ActionListDialogs />
      <div
        data-component="floating-table-actions"
        className={cn(
          "animate-in slide-in-from-bottom-2 duration-200",
          "bg-midnight text-midnight-foreground shadow-lg",
          "fixed left-0 bottom-0 border-0 z-40 md:absolute md:min-w-28 md:-ml-4 md:z-50 md:bottom-6 md:rounded-lg md:left-1/2 md:-translate-x-1/2",
          "flex flex-col gap-3 px-4 py-3",
          "w-full md:w-full max-w-max md:@xl/tabs-content:w-[80%] md:@xl/tabs-content:flex-row md:@xl/tabs-content:items-center md:@xl/tabs-content:gap-3 md:@xl/tabs-content:px-4 md:@xl/tabs-content:py-3 md:@xl/tabs-content:flex-wrap",
          className,
        )}
      >
        {/* Top section with count, itemType, select all, and clear */}
        <div className="flex items-center justify-between w-full gap-2">
          <SelectedItems />
          <div className="hidden md:@lg/tabs-content:block h-8 w-[1px] bg-gray-700" />
          <ActionListButtons className="hidden md:@lg/tabs-content:flex" />
          <div className="hidden md:@lg/tabs-content:block h-8 w-[1px] bg-gray-700" />
          <ClearSelection />
        </div>
        <div className="md:@lg/tabs-content:hidden w-full h-[1px] bg-gray-700"></div>
        <ActionListButtons className="md:@lg/tabs-content:hidden" />
      </div>
    </>
  );
};
